<template>
  <!-- ── FT-1474: THE CARD LAB (Cp) — view the tarot-style LotCard
       (src/components/LotCard.vue) live, fed a real Lot from roles.json, AND
       tune it on dials. Every dial sets a CSS custom property on the card's
       wrapper; those inherit into the scoped .lot-card and its use-sites read
       them with a fallback, so the card still renders correctly standalone
       (no lab) — the lab only OVERRIDES the shipped defaults.

       DEV-ONLY, ADDITIVE: renders LotCard read-only with a picked role — no
       store reads or writes, no effect on the town square or the existing
       hover. Stable className hooks: .card-preview-lab, .cp-toggle, .cp-role,
       .cp-stage, .cp-card.

       ELEVENTH DOOR in App.vue's dev column, at 378px, behind
       `devLabs && labsListOpen` like the rest of the column. Open, it breaks
       out wide: a scrollable dial column beside a LARGE live card. -->
  <div
    id="card-preview-lab"
    class="card-preview-lab"
    :class="{ open: cpLabOpen }"
  >
    <button
      type="button"
      class="cp-toggle"
      title="Card lab — the tarot-style Lot card, live, fed a real Lot, on dials"
      :aria-expanded="String(cpLabOpen)"
      @click="cpLabOpen = !cpLabOpen"
    >
      Card lab
    </button>
    <div class="cp-rows" v-if="cpLabOpen">
      <!-- the dial column -->
      <div class="cp-panel">
        <select
          class="cp-role"
          :value="cpRoleId"
          title="Which Lot to preview — every role that has both a light and a shadow ability"
          @change="cpRoleId = $event.target.value"
        >
          <option v-for="r in cpRoles" :key="r.id" :value="r.id">
            {{ r.name }}
          </option>
        </select>

        <select
          class="cp-role cp-palette"
          :value="cpPaletteId"
          title="Colour scheme — flip between the two Reader palettes to compare them live on the card"
          @change="cpPaletteId = $event.target.value"
        >
          <option v-for="p in cpPalettes" :key="p.id" :value="p.id">
            {{ p.label }}
          </option>
        </select>

        <!-- Light-hue comparison (FT-1472 rev8): swap the LIGHT band's hue only -->
        <div class="cp-hue" role="group" aria-label="Light band hue">
          <button
            v-for="h in cpLightHues"
            :key="h.id"
            type="button"
            class="cp-hue-chip"
            :class="{ active: cpLightHueId === h.id }"
            :title="'Light band hue: ' + h.label"
            @click="cpLightHueId = h.id"
          >
            <span
              class="cp-hue-dot"
              :style="{ background: h.color }"
              aria-hidden="true"
            ></span>
            {{ h.label }}
          </button>
        </div>

        <template v-for="group in cpGroups">
          <div class="cp-head" :key="group + '-head'">{{ group }}</div>
          <div class="cp-row" v-for="d in cpGroupDials(group)" :key="d.key">
            <span class="cp-label" :title="d.hint">{{ d.label }}</span>
            <label v-if="d.toggle" class="cp-check">
              <input
                type="checkbox"
                :checked="dials[d.key]"
                @change="dials[d.key] = $event.target.checked"
              />
            </label>
            <NumberScrub
              v-else
              :value="dials[d.key]"
              :min="d.min"
              :max="d.max"
              :step="d.step"
              :title="d.hint"
              @input="dials[d.key] = $event"
            />
          </div>
        </template>

        <button
          type="button"
          class="cp-reset"
          title="Back to the card's shipped defaults — every dial to the value LotCard renders with when no lab is present"
          @click="resetDials"
        >
          Reset
        </button>
      </div>

      <!-- the live card; the dials set CSS vars on this wrapper -->
      <div class="cp-stage">
        <div class="cp-card" :style="cardVars">
          <LotCard v-if="cpRole" :role="cpRole" :width="dials.size" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import rolesJSON from "../roles.json";
import LotCard from "./LotCard";
import NumberScrub from "./NumberScrub";

// PALETTE presets (FT-1470). Each sets the card's colour vars to a whole scheme
// so the two Reader looks can be judged live on the real card. `none` sets no
// colour vars, so LotCard falls back to its shipped defaults (silver light / gold
// name / slate shadow). Everything is driven through the existing `--lot-*` vars
// plus `--lot-name-color` and the two glow vars added this pass — the toggle just
// sets vars, so defaults with no lab stay the current shipped look.
const PALETTES = [
  { id: "none", label: "Shipped (no palette)", vars: {} },
  {
    id: "A",
    label: "A · Purple Reader",
    // Light box + sun → gold; shadow box + eclipse → deep cold teal; name → purple.
    vars: {
      "--lot-light-color": "#c8a24c",
      "--lot-light-icon-color": "#d9b86a",
      "--lot-shadow-color": "#2e8080",
      "--lot-shadow-border-color": "#3f9a9a",
      "--lot-name-color": "#9b7bc4",
    },
  },
  {
    id: "B",
    label: "B · Gold Reader",
    // Light box + sun → luminous pale cyan WITH a glow/bloom so it radiates as
    // light; shadow box + eclipse → violet; name → gold.
    vars: {
      "--lot-light-color": "#aedce8",
      "--lot-light-icon-color": "#c8ecf4",
      "--lot-shadow-color": "#8e74c0",
      "--lot-shadow-border-color": "#a38ed0",
      "--lot-name-color": "#c8a24c",
      // outer bloom on the light box — a wide soft cyan halo + a tighter core
      "--lot-light-glow":
        "0 0 26px rgba(174, 220, 232, 0.5), 0 0 9px rgba(200, 236, 244, 0.4)",
      // matching bloom on the sun glyph so it reads as a source, not a shape
      "--lot-sun-glow":
        "drop-shadow(0 0 6px rgba(200, 236, 244, 0.95)) drop-shadow(0 0 15px rgba(174, 220, 232, 0.65))",
    },
  },
];

// LIGHT-HUE presets (FT-1472 rev8). A small comparison control that swaps ONLY
// the light band's hue — it drives `--lot-light-color` (the fill + border + inner
// glow all color-mix off it), plus a matching `--lot-light-icon-color` and a
// hue-tinted `--lot-light-glow` bloom, all at the new subtle rev8 strength. It
// layers OVER the palette's colour vars (spread after them in cardVars), so it
// wins for the light hue while the palette still owns name/shadow. Shadow stays
// violet. Default = sky (the daylight blue reads best at the whisper strength).
const LIGHT_HUES = [
  {
    id: "cyan",
    label: "Cyan",
    color: "#aedce8",
    vars: {
      "--lot-light-color": "#aedce8",
      "--lot-light-icon-color": "#c8ecf4",
      "--lot-light-glow":
        "0 0 12px rgba(174, 220, 232, 0.1), 0 0 5px rgba(200, 236, 244, 0.08)",
    },
  },
  {
    id: "sky",
    label: "Sky",
    color: "#8fb8e8",
    vars: {
      "--lot-light-color": "#8fb8e8",
      "--lot-light-icon-color": "#bcd4f4",
      "--lot-light-glow":
        "0 0 12px rgba(143, 184, 232, 0.1), 0 0 5px rgba(190, 212, 244, 0.08)",
    },
  },
  {
    id: "white",
    label: "White",
    color: "#edf2f8",
    vars: {
      "--lot-light-color": "#edf2f8",
      "--lot-light-icon-color": "#f4f8fc",
      "--lot-light-glow":
        "0 0 12px rgba(237, 242, 248, 0.1), 0 0 5px rgba(244, 248, 252, 0.08)",
    },
  },
];

// The dial spec. Each entry drives ONE CSS custom property (or, for `size`, the
// card's width prop). `min/max/step` bound the NumberScrub; `toggle` swaps it
// for a checkbox. `def` is the SHIPPED default — the value the card renders with
// standalone, so Reset lands exactly on LotCard's own fallbacks.
const DIALS = [
  // Card
  {
    key: "size",
    group: "Card",
    label: "Size",
    hint: "Overall card width (px); height follows the base card's aspect",
    min: 220,
    max: 620,
    step: 10,
    def: 400,
  },
  // Light box
  {
    key: "lightBg",
    group: "Light box",
    label: "Fill",
    hint: "Warm fill opacity, % of the shipped glow",
    min: 0,
    max: 200,
    step: 5,
    def: 100,
  },
  {
    key: "lightBorder",
    group: "Light box",
    label: "Frame",
    hint: "Gold frame opacity, % of shipped",
    min: 0,
    max: 200,
    step: 5,
    def: 100,
  },
  // Shadow box
  {
    key: "shadowBg",
    group: "Shadow box",
    label: "Fill",
    hint: "Dark inset fill opacity, % of shipped",
    min: 0,
    max: 200,
    step: 5,
    def: 100,
  },
  {
    key: "shadowBorder",
    group: "Shadow box",
    label: "Frame",
    hint: "Frame opacity, % of shipped (top border stays the emphasis)",
    min: 0,
    max: 200,
    step: 5,
    def: 100,
  },
  {
    key: "shadowTopOnly",
    group: "Shadow box",
    label: "Top only",
    hint: "Fade the three non-top borders away — a top-border hairline instead of a full box",
    toggle: true,
    def: false,
  },
  // Relief — the neumorphic depth, scaling the raised light box + the sunken
  // shadow box together (drives --lot-depth). 100% = the subtle shipped amount.
  {
    key: "depth",
    group: "Relief",
    label: "Depth",
    hint: "Neumorphic relief strength — the raised light box + sunken shadow box together, % of shipped",
    min: 0,
    max: 250,
    step: 5,
    def: 100,
  },
  // Ability text
  {
    key: "abilitySize",
    group: "Ability text",
    label: "Font",
    hint: "Ability text font size (px)",
    min: 10,
    max: 24,
    step: 0.5,
    def: 16,
  },
  {
    key: "abilityPad",
    group: "Ability text",
    label: "Padding",
    hint: "Ability box vertical padding (px); horizontal is +2",
    min: 4,
    max: 28,
    step: 1,
    def: 13,
  },
  // Gutters
  {
    key: "sunTop",
    group: "Gutters",
    label: "Sun",
    hint: "Sun offset from the top edge (%)",
    min: 0,
    max: 9,
    step: 0.2,
    def: 2.6,
  },
  {
    key: "eclipseBottom",
    group: "Gutters",
    label: "Eclipse",
    hint: "Eclipse offset from the bottom edge (%)",
    min: 0,
    max: 9,
    step: 0.2,
    def: 1.4,
  },
  // Name
  {
    key: "nameY",
    group: "Name",
    label: "Y offset",
    hint: "Slide the name down — from just under the light box down through the art (px)",
    min: 0,
    max: 340,
    step: 2,
    def: 0,
  },
  {
    key: "nameSize",
    group: "Name",
    label: "Font",
    hint: "Name font size (px)",
    min: 16,
    max: 60,
    step: 1,
    def: 32,
  },
];

export default {
  name: "CardPreviewLab",
  components: { LotCard, NumberScrub },
  data() {
    // The Lots: every role with BOTH a light and a shadow ability written —
    // the only ones LotCard has real text to show top and bottom.
    const roles = rolesJSON.filter((r) => r.ability && r.shadowAbility);
    const dials = {};
    DIALS.forEach((d) => {
      dials[d.key] = d.def;
    });
    return {
      cpLabOpen: false,
      cpRoles: roles,
      cpRoleId: (roles[0] && roles[0].id) || "",
      cpDials: DIALS,
      cpPalettes: PALETTES,
      cpPaletteId: "none",
      cpLightHues: LIGHT_HUES,
      cpLightHueId: "sky",
      dials,
    };
  },
  computed: {
    cpRole() {
      return (
        this.cpRoles.find((r) => r.id === this.cpRoleId) || this.cpRoles[0]
      );
    },
    /** The group heads, in dial order, each once. */
    cpGroups() {
      const seen = [];
      this.cpDials.forEach((d) => {
        if (seen.indexOf(d.group) === -1) seen.push(d.group);
      });
      return seen;
    },
    /** The dials as CSS custom properties on the card wrapper. Percent dials
     *  become 0–2 multipliers; px dials carry their unit; the two gutter
     *  offsets and the name-Y stay unitless (the card's calc()s add the unit).
     *  The toggle maps to `--lot-shadow-sides`: 1 = full box, 0 = top only. */
    cardVars() {
      const d = this.dials;
      const palette =
        this.cpPalettes.find((p) => p.id === this.cpPaletteId) ||
        this.cpPalettes[0];
      const lightHue =
        this.cpLightHues.find((h) => h.id === this.cpLightHueId) ||
        this.cpLightHues[0];
      return {
        // the picked palette's colour vars first; the dial vars below never
        // collide with them, so both layers coexist on the one wrapper.
        ...palette.vars,
        // the light-hue comparison layers OVER the palette — it owns the light
        // band's colour/glow so the three hues are swappable against any palette.
        ...lightHue.vars,
        "--lot-light-bg-opacity": d.lightBg / 100,
        "--lot-light-border-opacity": d.lightBorder / 100,
        "--lot-shadow-bg-opacity": d.shadowBg / 100,
        "--lot-shadow-border-opacity": d.shadowBorder / 100,
        "--lot-shadow-sides": d.shadowTopOnly ? 0 : 1,
        "--lot-depth": d.depth / 100,
        "--lot-ability-font-size": d.abilitySize + "px",
        "--lot-ability-padding": d.abilityPad + "px",
        "--lot-sun-top": d.sunTop,
        "--lot-eclipse-bottom": d.eclipseBottom,
        "--lot-name-y": d.nameY,
        "--lot-name-size": d.nameSize + "px",
      };
    },
  },
  methods: {
    cpGroupDials(group) {
      return this.cpDials.filter((d) => d.group === group);
    },
    resetDials() {
      this.cpDials.forEach((d) => {
        this.dials[d.key] = d.def;
      });
    },
  },
};
</script>

<style scoped lang="scss">
// TEMPORARY, DELETE ME — the dev column's shared shell (StatsPlateLab's own
// class names, renamed): left-docked plate, bone-on-dark voice. Open, the rows
// break out wide: a scrollable dial column beside a large live card.
#card-preview-lab {
  position: fixed;
  top: 378px;
  left: 0;
  z-index: 60;
  display: flex;
  align-items: flex-start;
  font-size: 13px;

  // not on a phone, for the column's shared reason: the ring is a rectangle
  // there and the toggle lands on real controls.
  @media (pointer: coarse) {
    display: none;
  }

  .cp-toggle {
    width: 132px;
    height: 22px;
    line-height: 20px;
    padding: 0 10px;
    box-sizing: border-box;
    text-align: left;
    white-space: nowrap;
    font-family: inherit;
    font-size: 12px;
    color: #d8cdb4;
    background: rgba(0, 0, 0, 0.6);
    border: 1px solid #3d3d3d;
    border-left: none;
    border-radius: 0 6px 6px 0;
    cursor: pointer;
    opacity: 0.45;
    &:hover,
    &:focus-visible {
      opacity: 1;
      border-color: rgba(150, 130, 175, 0.75);
      outline: none;
    }
  }
  &.open .cp-toggle {
    opacity: 1;
    border-color: rgba(150, 130, 175, 0.75);
  }

  .cp-rows {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: 14px;
    padding: 10px;
    background: rgba(8, 6, 10, 0.92);
    border: 1px solid rgba(120, 105, 135, 0.45);
    border-left: none;
    border-radius: 0 8px 8px 0;
    max-height: calc(100vh - 388px);
  }

  // the dial column — scrolls on its own if it runs past the card's height
  .cp-panel {
    display: flex;
    flex-direction: column;
    gap: 6px;
    width: 176px;
    flex: 0 0 auto;
    max-height: calc(100vh - 408px);
    overflow-y: auto;
    padding-right: 4px;
  }
  .cp-role {
    font-family: inherit;
    font-size: 12px;
    color: #d8cdb4;
    background: rgba(20, 16, 22, 0.9);
    border: 1px solid rgba(120, 105, 135, 0.4);
    border-radius: 5px;
    padding: 2px 4px;
    max-width: 100%;
    margin-bottom: 2px;
    cursor: pointer;
    &:hover,
    &:focus-visible {
      border-color: rgba(150, 130, 175, 0.75);
      outline: none;
    }
  }
  .cp-head {
    display: flex;
    align-items: baseline;
    gap: 6px;
    font-size: 10px;
    letter-spacing: 0.09em;
    text-transform: uppercase;
    color: #b6a8c8;
    opacity: 0.85;
    padding-bottom: 3px;
    border-bottom: 1px solid rgba(120, 105, 135, 0.28);
    &:not(:first-child) {
      margin-top: 4px;
    }
  }
  .cp-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    font-size: 12px;
    color: #d8cdb4;
  }
  .cp-label {
    white-space: nowrap;
    opacity: 0.7;
    cursor: help;
  }
  .cp-check {
    display: inline-flex;
    align-items: center;
    cursor: pointer;
    input {
      accent-color: rgba(150, 130, 175, 0.9);
      cursor: pointer;
    }
  }
  .cp-reset {
    align-self: flex-start;
    margin-top: 6px;
    font-family: inherit;
    font-size: 11px;
    color: #d8cdb4;
    background: rgba(20, 16, 22, 0.9);
    border: 1px solid rgba(120, 105, 135, 0.4);
    border-radius: 5px;
    padding: 2px 8px;
    cursor: pointer;
    &:hover,
    &:focus-visible {
      border-color: rgba(150, 130, 175, 0.75);
      outline: none;
    }
  }

  // the stage — holds the large live card; scrolls if the card is taller than
  // the panel so a big card is never clipped away.
  .cp-stage {
    flex: 0 0 auto;
    max-height: calc(100vh - 408px);
    overflow: auto;
  }
  .cp-card {
    // the dials land here as CSS custom properties (see cardVars); they inherit
    // down into the scoped .lot-card, whose use-sites read them with fallbacks.
  }

  // Light-hue chips (FT-1472 rev8) — a 3-up row to compare the light band's hue.
  .cp-hue {
    display: flex;
    gap: 4px;
    margin-bottom: 2px;
  }
  .cp-hue-chip {
    flex: 1 1 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
    font-family: inherit;
    font-size: 11px;
    color: #d8cdb4;
    background: rgba(20, 16, 22, 0.9);
    border: 1px solid rgba(120, 105, 135, 0.4);
    border-radius: 5px;
    padding: 3px 4px;
    cursor: pointer;
    &:hover,
    &:focus-visible {
      border-color: rgba(150, 130, 175, 0.75);
      outline: none;
    }
    &.active {
      border-color: rgba(180, 160, 205, 0.95);
      background: rgba(40, 32, 46, 0.95);
    }
  }
  .cp-hue-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    border: 1px solid rgba(0, 0, 0, 0.4);
    flex: 0 0 auto;
  }

  // the scrub's "seat" preset inherits its colour, which reads on a coin and
  // disappears against this panel's own ground.
  .num-scrub-box {
    color: #d8cdb4;
  }
}
</style>
